import type { DocumentItem } from "@/types/ui-models";
import { dash, fmtDate, isExpired } from "../lib/docUtils";
import DocumentTypeBadge from "./DocumentTypeBadge";
import DocumentStatusBadge from "./DocumentStatusBadge";

interface DocumentDetailDrawerProps {
  document: DocumentItem | null;
  onClose: () => void;
}

export default function DocumentDetailDrawer({ document: doc, onClose }: DocumentDetailDrawerProps) {
  if (!doc) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose} />
      <div className="fixed right-0 top-0 bottom-0 w-full max-w-lg bg-white z-50 shadow-xl overflow-y-auto">
        <div className="p-6">
          <div className="flex items-start justify-between mb-6">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <DocumentTypeBadge type={doc.type} size="md" />
                <DocumentStatusBadge status={doc.status} size="md" />
              </div>
              <h2 className="text-lg font-bold text-foreground-900 font-heading mt-2">{doc.title}</h2>
              <div className="text-xs text-foreground-500 font-mono mt-1">{dash(doc.number)}</div>
            </div>
            <button onClick={onClose} className="w-8 h-8 rounded-md hover:bg-background-100 flex items-center justify-center transition-colors" aria-label="Fechar">
              <i className="ri-close-line w-full h-full flex items-center justify-center text-lg text-foreground-500" />
            </button>
          </div>

          <div className="bg-background-50 rounded-lg p-4 mb-5">
            <div className="text-xs font-semibold text-foreground-700 mb-2">Descrição</div>
            <p className="text-sm text-foreground-600 leading-relaxed">{doc.description || "Sem descrição registrada."}</p>
          </div>

          <div className="grid grid-cols-2 gap-3 mb-5">
            <div className="bg-background-50 rounded-lg p-3">
              <div className="text-[11px] text-foreground-400 mb-0.5">Projeto</div>
              <div className="text-sm font-medium text-foreground-800">{doc.projectName || "—"}</div>
            </div>
            <div className="bg-background-50 rounded-lg p-3">
              <div className="text-[11px] text-foreground-400 mb-0.5">Vínculo principal</div>
              <div className="text-sm font-medium text-foreground-800">{dash(doc.primaryBinding)}</div>
            </div>
            <div className="bg-background-50 rounded-lg p-3">
              <div className="text-[11px] text-foreground-400 mb-0.5">Órgão emissor</div>
              <div className="text-sm font-medium text-foreground-800">{dash(doc.issuingBody)}</div>
            </div>
            <div className="bg-background-50 rounded-lg p-3">
              <div className="text-[11px] text-foreground-400 mb-0.5">Emissão</div>
              <div className="text-sm font-medium text-foreground-800">{fmtDate(doc.issueDate)}</div>
            </div>
            <div className="bg-background-50 rounded-lg p-3">
              <div className="text-[11px] text-foreground-400 mb-0.5">Validade</div>
              <div className={`text-sm font-medium ${isExpired(doc) ? "text-danger-600" : "text-foreground-800"}`}>{doc.expiryDate ? fmtDate(doc.expiryDate) : "Não informada"}</div>
            </div>
            <div className="bg-background-50 rounded-lg p-3">
              <div className="text-[11px] text-foreground-400 mb-0.5">Tipo de arquivo</div>
              <div className="text-sm font-medium text-foreground-800">{doc.hasFile ? dash(doc.fileType) : "Sem arquivo anexado"}</div>
            </div>
          </div>

          {doc.replacedById && (
            <div className="bg-warning-50 border border-warning-200 rounded-lg p-3 mb-5">
              <div className="text-xs font-semibold text-warning-700 mb-1">
                <i className="ri-swap-line w-4 h-4 inline-flex items-center justify-center mr-1" />
                Documento substituído
              </div>
              <div className="text-xs text-warning-600">Este documento foi substituído por uma versão mais recente (ID: {doc.replacedById}).</div>
            </div>
          )}

          {doc.gisMetadata && (
            <div className="mb-5">
              <div className="text-sm font-semibold text-foreground-900 mb-3">Metadados GIS</div>
              <div className="bg-accent-50 rounded-lg p-4 border border-accent-100 grid grid-cols-2 gap-3">
                <div>
                  <div className="text-[11px] text-accent-600">SRID</div>
                  <div className="text-sm font-medium text-accent-800">{doc.gisMetadata.srid}</div>
                </div>
                <div>
                  <div className="text-[11px] text-accent-600">Geometria</div>
                  <div className="text-sm font-medium text-accent-800">{doc.gisMetadata.geometry}</div>
                </div>
                <div>
                  <div className="text-[11px] text-accent-600">Feições</div>
                  <div className="text-sm font-medium text-accent-800">{doc.gisMetadata.featuresCount.toLocaleString("pt-BR")}</div>
                </div>
                <div>
                  <div className="text-[11px] text-accent-600">BBox</div>
                  <div className="text-sm font-medium text-accent-800 font-mono text-xs">{doc.gisMetadata.bbox}</div>
                </div>
              </div>
            </div>
          )}

          {doc.ctaCode && (
            <div className="bg-primary-50 border border-primary-100 rounded-lg p-3 mb-5">
              <div className="text-xs font-semibold text-primary-700 mb-1">Vínculo CTA</div>
              <div className="text-sm font-medium text-primary-800 font-mono">{doc.ctaCode}</div>
              {doc.areaName && <div className="text-xs text-primary-600 mt-1">{doc.areaName}</div>}
            </div>
          )}

          {doc.additionalBindings.length > 0 && (
            <div className="mb-5">
              <div className="text-sm font-semibold text-foreground-900 mb-2">Vínculos adicionais</div>
              <div className="flex flex-wrap gap-2">
                {doc.additionalBindings.map((b) => (
                  <span key={b} className="px-2 py-1 text-xs font-medium bg-background-50 text-foreground-600 rounded-md border border-background-200">{b}</span>
                ))}
              </div>
            </div>
          )}

          {doc.tags.length > 0 && (
            <div className="mb-5">
              <div className="text-sm font-semibold text-foreground-900 mb-2">Tags</div>
              <div className="flex flex-wrap gap-1.5">
                {doc.tags.map((t) => (
                  <span key={t} className="px-2 py-1 text-xs font-medium bg-background-100 text-foreground-500 rounded-full">{t}</span>
                ))}
              </div>
            </div>
          )}

          <div className="bg-background-50 rounded-lg p-4 mb-5">
            <div className="text-xs font-semibold text-foreground-700 mb-2">Histórico</div>
            <div className="space-y-2 text-xs">
              <div className="flex items-start gap-2">
                <i className="ri-calendar-check-line text-primary-600 w-4 h-4 flex items-center justify-center shrink-0" />
                <div><span className="font-medium text-foreground-700">Criado em</span><span className="text-foreground-400 ml-2">{fmtDate(doc.createdAt)}</span></div>
              </div>
              <div className="flex items-start gap-2">
                <i className="ri-refresh-line text-accent-600 w-4 h-4 flex items-center justify-center shrink-0" />
                <div><span className="font-medium text-foreground-700">Atualizado em</span><span className="text-foreground-400 ml-2">{fmtDate(doc.updatedAt)}</span></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}